
import React from "react";
import { Button, Card, Col, Form, Row } from "banco-components";
import { FormProvider } from "react-hook-form";
import { useTermsCalculations } from "banco/hooks/useTermsCalculations/useTermsCalculations";
import { formatMoney } from "banco/util/formatter";
import styles from "./styles/v4LoanCalculator.module.scss";
import { useBrandConfig } from "banco/contexts/BrandConfigContext";
import {
  useLoanCalculatorForm,
  useSliderPercentages,
  RepresentativeExampleFooter,
  RangeSlider,
  PurposeSelector
} from "../../shared/LoanCalculatorShared";

export const V4LoanCalculator = () => {
  const {
    form_methods,
    desired_advance,
    tenure,
    purpose,
    loan_calc_constants
  } = useLoanCalculatorForm();

  const { display_direct_booking_rep_apr_note, trading_name } = useBrandConfig();
  const { setValue } = form_methods;

  const term_calcs = useTermsCalculations(
    window?.static_data?.direct_new_rep_products || [], Number(desired_advance), tenure
  );

  const no_dec_money = (val?: number | string) => formatMoney(val, "$0,0", true);

  const { desired_advance_pct, tenure_pct } = useSliderPercentages(
    desired_advance,
    tenure,
    loan_calc_constants
  );

  return (
    <Card className={ `${styles["loan-calculator"]} representative-example` }
      data-fb-panel="Representative Example">
      <FormProvider { ...form_methods }>
        <Card.Body>
          <Row>
            <Col xs={ 12 } md={ 6 }>
              <Form.Group>
                <Form.Label className="justify-content-center" htmlFor="desired_advance_slider">
                  How much would you like to borrow?
                </Form.Label>
                <span className="range-value">
                  { no_dec_money(desired_advance) }
                </span>
                <RangeSlider
                  id="desired_advance_slider"
                  name="desired_advance"
                  value={ desired_advance }
                  min={ loan_calc_constants.min_loan_value }
                  max={ loan_calc_constants.max_loan_value }
                  step={ loan_calc_constants.loan_value_step }
                  percentage={ desired_advance_pct }
                  onChange={ (value) => setValue("desired_advance", value, { shouldDirty: true }) }
                  dataFbInput="desired_advance_slider"
                />
              </Form.Group>
            </Col>
            <Col xs={ 12 } md={ 6 }>
              <Form.Group>
                <Form.Label className="justify-content-center" htmlFor="tenure_slider">
                  For how many months?
                </Form.Label>
                <span className="range-value">
                  { tenure }
                </span>
                <RangeSlider
                  id="tenure_slider"
                  name="tenure"
                  value={ tenure }
                  min={ loan_calc_constants.loan_tenures_min }
                  max={ loan_calc_constants.loan_tenures_max }
                  step={ loan_calc_constants.loan_tenures_step }
                  percentage={ tenure_pct }
                  onChange={ (value) => setValue("tenure", value, { shouldDirty: true }) }
                  dataFbInput="tenure_slider"
                />
              </Form.Group>
            </Col>
          </Row>
          <Row>
            <Col xs={ 12 } lg={ 7 }>
              <Form.Group>
                <PurposeSelector
                  value={ purpose }
                  onChange={ (value) => setValue("purpose", value, { shouldDirty: true }) }
                />
              </Form.Group>
            </Col>
            <Col xs={ 12 } lg={ 5 } className="align-content-center">
              <Form.Group>
                <Button
                  variant="primary"
                  href="/apply?hide_rep_apr=true"
                  data-fb-button="Apply Button"
                  className={ styles["apply-button"] }
                >
                  <svg width="24" height="24" className={ styles["apply-button-svg"] }>
                    <use xlinkHref="#apply"></use>
                  </svg>
                  <span>
                    Apply Now
                  </span>
                </Button>
              </Form.Group>
            </Col>
          </Row>
          <p className="mt-2">
            The rate you are offered will depend on your personal circumstances. <br/>
            Getting a quote won't affect your credit score.
          </p>
        </Card.Body>
        <div className={ styles["card-table"] }>
          <table className="table">
            <tbody>
              <tr>
                <td className={ styles["card-table-cell"] }>
                  <span className={ styles["table-label"] }>Monthly&nbsp;repayments</span>
                  <span className={ styles["table-value"] }>
                    { formatMoney(term_calcs?.approximateMonthlyPayment) }
                  </span>
                </td>
                <td className={ styles["card-table-cell"] }>
                  <span className={ styles["table-label"] }>Representative</span>
                  <span className={ styles["table-value"] }>
                    { term_calcs?.product?.apr }% APR
                  </span>
                </td>
                <td className={ styles["card-table-cell"] }>
                  <span className={ styles["table-label"] }>Interest</span>
                  <span className={ styles["table-value"] }>
                    { formatMoney(term_calcs?.interestAndFees) }
                  </span>
                </td>
                <td className={ styles["card-table-cell"] }>
                  <span className={ styles["table-label"] }>Total&nbsp;to&nbsp;repay</span>
                  <span className={ styles["table-value"] }>
                    { formatMoney(term_calcs?.totalRepayable) }
                  </span>
                </td>
              </tr>
            </tbody>
            <tfoot>
              <tr>
                <th colSpan={ 4 }>Representative Example</th>
              </tr>
              <RepresentativeExampleFooter
                loan_calc_constants={ loan_calc_constants }
                display_direct_booking_rep_apr_note={ display_direct_booking_rep_apr_note }
                trading_name={ trading_name }
              />
            </tfoot>
          </table>
        </div>
      </FormProvider>
    </Card>
  );
};
